import {AbstractLayer} from '../core/layers/abstract.layer';
import {AbstractParser} from './parsers/abstract-parser';
import {SourceMapParser} from './parsers/source-map-parser';
import displays, {Display} from '/src/core/displays';
import {CircleLayer} from '/src/core/layers/circle.layer';
import {EllipseLayer} from '/src/core/layers/ellipse.layer';
import {TextLayer} from '/src/core/layers/text.layer';
import {PlatformTemplates} from '/src/types';
import {packedHexColor565, toCppVariableName} from '/src/utils';

export type TPlatformFeatures = {
    hasCustomFontSize: boolean;
    hasInvertedColors: boolean;
    hasRGBSupport: boolean;
    defaultColor: string;
    screenBgColor?: string;
    hasIndexedColors?: boolean;
    hasRoundCorners?: boolean;
    palette?: string[];
    interfaceColors: {
        selectColor: string;
        resizeIconColor: string;
        hoverColor: string;
        rulerColor: string;
        rulerLineColor: string;
        selectionStrokeColor: string;
    };
    smooth?: boolean;
    isHidden?: boolean;
};

/**
 * Abstract platform definition.
 */
export abstract class Platform {
    static id: string;
    protected fonts: TPlatformFont[];
    protected name: string;
    protected description: string;
    public features: TPlatformFeatures = {
        hasCustomFontSize: false,
        hasInvertedColors: false,
        hasRGBSupport: false,
        defaultColor: '#FFFFFF',
        screenBgColor: '#000000',
        hasIndexedColors: false,
        hasRoundCorners: true,
        palette: [],
        interfaceColors: {
            selectColor: 'rgba(255, 255, 255, 0.9)',
            resizeIconColor: 'rgba(255, 255, 255, 0.6)',
            hoverColor: 'rgba(255, 255, 255, 0.5)',
            rulerColor: 'rgba(255, 255, 255, 0.5)',
            rulerLineColor: 'rgba(255, 255, 255, 0.5)',
            selectionStrokeColor: 'rgba(255, 255, 255, 0.5)',
        },
        smooth: false,
        isHidden: false,
    };

    protected templates: PlatformTemplates;
    protected currentTemplate: string = 'Default';
    protected settings = {};
    protected parser: AbstractParser;
    public displays: Display[] = displays;
    public sourceMapParser: SourceMapParser = new SourceMapParser();

    abstract generateSourceCode(layers: AbstractLayer[], ctx?: OffscreenCanvasRenderingContext2D): string;

    importSourceCode(sourceCode: string): {states: any[]; warnings: string[]} {
        return this.parser.importSourceCode(sourceCode);
    }

    processLayerModifiers(layer: AbstractLayer, props: any, overrides: any = {}) {
        const modifyPropsHandlers = {
            x: (layer: AbstractLayer, props: any) => {
                if (layer instanceof CircleLayer) {
                    props.x = layer.position.x + layer.radius;
                } else if (layer instanceof EllipseLayer) {
                    props.x = layer.position.x + layer.rx;
                } else {
                    props.x = layer.position.x;
                }
            },
            y: (layer: AbstractLayer, props: any) => {
                if (layer instanceof CircleLayer) {
                    props.y = layer.position.y + layer.radius;
                } else if (layer instanceof EllipseLayer) {
                    props.y = layer.position.y + layer.ry;
                } else if (layer instanceof TextLayer) {
                    props.y = this.getTextPosition(props)[1];
                } else {
                    props.y = layer.position.y;
                }
            },
            w: (layer: AbstractLayer, props: any) => {
                props.w = layer.size.x;
            },
            h: (layer: AbstractLayer, props: any) => {
                props.h = layer.size.y;
            },
            x1: (layer: AbstractLayer, props: any) => {
                props.x1 = layer.p1.x;
            },
            y1: (layer: AbstractLayer, props: any) => {
                props.y1 = layer.p1.y;
            },
            x2: (layer: AbstractLayer, props: any) => {
                props.x2 = layer.p2.x;
            },
            y2: (layer: AbstractLayer, props: any) => {
                props.y2 = layer.p2.y;
            },
            rx: (layer: AbstractLayer, props: any) => {
                props.rx = layer.rx;
            },
            ry: (layer: AbstractLayer, props: any) => {
                props.ry = layer.ry;
            },
            fontSize: (layer: AbstractLayer, props: any) => {
                props.fontSize = layer.scaleFactor;
            },
            color: (layer: AbstractLayer, props: any) => {
                props.color = this.packColor(layer.color);
            },
            text: (layer: AbstractLayer, props: any) => {
                props.text = props.text.replace(/"/g, '\\"');
            },
            ...overrides,
        };

        Object.keys(layer.modifiers).forEach((name) => {
            if (modifyPropsHandlers[name]) {
                modifyPropsHandlers[name](layer, props);
            }
        });
    }

    protected processVarDeclarations(layer: AbstractLayer, props: any, declarations: {type: string; data: any}[]) {
        Object.keys(layer.variables).forEach((name) => {
            if (layer.variables[name]) {
                const varName = toCppVariableName(`${layer.name}_${name}`);
                declarations.push({
                    type: 'var',
                    data: {
                        name: varName,
                        value: props[name],
                        type: layer.modifiers[name].type,
                    },
                });
                props[name] = varName;
                if (name === 'text') {
                    props.isTextVariable = true;
                }
            }
        });
        return declarations.sort((a, b) => a.data.name.localeCompare(b.data.name));
    }

    packColor(color: string): string {
        return packedHexColor565(color);
    }

    getTextPosition(layer: TextLayer) {
        return [layer.position[0], layer.position[1] - layer.bounds[3]];
    }

    public getTemplate(): string {
        return this.currentTemplate;
    }

    public getTemplateSettings(): any {
        return this.templates[this.currentTemplate].settings;
    }

    public getTemplates(): any {
        return this.templates;
    }

    public setTemplate(templateName: string): void {
        this.currentTemplate = templateName;
    }

    public setTemplateSetting(name: string, value: boolean): void {
        this.templates[this.currentTemplate].settings[name] = value;
    }

    public getFonts(): TPlatformFont[] {
        return this.fonts;
    }

    public getName(): string {
        return this.name;
    }

    public getDescription(): string {
        return this.description;
    }

    public hasParser(): boolean {
        return !!this.parser;
    }
}
